import React, { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";

// Types
import {
  IAppointmentCreatePayload,
  IAppointmentTicketTimeSlot,
} from "~/types/appointment";
// Components

import AppointmentTimeCard from "~/components/cards/time/AppointmentTimeCard";
import AppointmentConfirmModal from "~/components/modals/appointment/AppointmentConfirmModal";
import SuccessModal from "~/components/modals/SuccessModal";

import s from "./CurrentAvailableAppointments.module.scss";
import RootStore from "~/store/RootStore";
import { useStore } from "~/hooks/useStore";
import { useNavigate } from "react-router-dom";
import { ResponseStatusCode } from "~/types/request";
import AppointmentErrorModal from "~/components/modals/appointment/AppointmentErrorModal";
import { logEvent } from "~/utils/amplitude";
import ROUTES from "~/utils/routes";

interface Props {
  tickets: IAppointmentTicketTimeSlot[];
}

const CurrentAvailableAppointments: React.FC<Props> = observer(
  ({ tickets }) => {
    const store: RootStore = useStore();
    const [isConfirmModalOpen, setIsConfirmModalOpen] =
      useState<boolean>(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] =
      useState<boolean>(false);
    const navigate = useNavigate();

    const isErrorOnAssign = store.egisErrorStore.errorOnAssign.isError;
    const errorOnAssignText = store.egisErrorStore.errorOnAssign.errorMessage;

    const formalizeAppearedErrorInModal = (errorMessage: string) => {
      store.egisErrorStore.errorOnAssign.setErrorMessage(errorMessage);
      store.egisErrorStore.errorOnAssign.setIsError(true);
    };

    const resetAppearedError = () => {
      logEvent("Д/ Нажал кнопку Закрыть / Ошибка записи", {});
      store.egisErrorStore.errorOnAssign.reset();
    };

    const appointOnTime = (
      externalTicketTimeSlot: IAppointmentTicketTimeSlot
    ) => {
      logEvent("Д/ Нажал на талон", {});
      store.appointmentStore.setCurrentTicketTimeSlot(externalTicketTimeSlot);
      setIsConfirmModalOpen(true);
    };

    const handleConfirm = (payload: IAppointmentCreatePayload) => {
      logEvent("Д/ Нажал кнопку подтвердить / Подтверждение записи", {});

      store.appointmentStore.createAppointment(payload).then((data) => {
        if (data && data.code !== ResponseStatusCode.EGISError) {
          logEvent("М/ Успешная запись", {});
          setIsConfirmModalOpen(false);
          handleOpenSuccess();
        } else if (data && data.message) {
          setIsConfirmModalOpen(false);
          formalizeAppearedErrorInModal(data.message);
        }
      });
    };

    const handleCloseConfirm = () => {
      logEvent("Д/ Нажал кнопку отменить / Подтверждение записи", {});
      setIsConfirmModalOpen(false);
    };

    const handleOpenSuccess = () => {
      setIsSuccessModalOpen(true);
    };

    const handleCloseSuccess = () => {
      setIsSuccessModalOpen(false);
      navigate(ROUTES.JOURNAL.PATHNAME);
    };

    useEffect(() => {
      !!isConfirmModalOpen && logEvent("М/ Окно подтверждения записи", {});

      !!!isConfirmModalOpen &&
        logEvent("Д/ Закрыл окно / Подтверждение записи", {});
    }, [isConfirmModalOpen]);

    useEffect(() => {
      !!isErrorOnAssign &&
        logEvent("О/ Ошибка записи", {
          "Сообщение в ошибке": errorOnAssignText,
        });

      !!!isErrorOnAssign &&
        logEvent("Д/ Закрыл окно / Ошибка записи", {
          "Сообщение в ошибке": errorOnAssignText,
        });
    }, [isErrorOnAssign, errorOnAssignText]);

    const handleErrorModalBtn = () => {
      logEvent("Д/ Нажал кнопку Сообщить о проблеме / Ошибка записи", {
        "Сообщение в ошибке": errorOnAssignText,
      });
    };

    return (
      <div className={s.cardsContainer}>
        {tickets.map((data) => {
          return (
            <AppointmentTimeCard
              {...data}
              key={data.id}
              onClick={() => appointOnTime(data)}
            />
          );
        })}

        <AppointmentConfirmModal
          isOpened={isConfirmModalOpen}
          onClose={handleCloseConfirm}
          onConfirm={handleConfirm}
        />
        <AppointmentErrorModal
          isOpened={isErrorOnAssign}
          onClose={resetAppearedError}
          onConfirm={handleErrorModalBtn}
          message={errorOnAssignText}
        />
        <SuccessModal
          isOpened={isSuccessModalOpen}
          onClose={handleCloseSuccess}
        />
      </div>
    );
  }
);

export default CurrentAvailableAppointments;
